import type {
  ApiKey,
  CreateApiKey,
  CreateWebhook,
  CreatedApiKey,
  UpdateWebhook,
  Webhook,
  WebhookDelivery,
  WebhookWithSecret,
} from '@cyberscore/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useSession } from '../../lib/session';

/** API keys and webhooks (task 71). */

const key = (t?: string) => ['integrations', t] as const;

export function useApiKeys(enabled = true) {
  const { tenant } = useSession();
  return useQuery({ queryKey: [...key(tenant?.id), 'keys'], queryFn: () => api<ApiKey[]>('GET', '/settings/api-keys'), enabled: !!tenant && enabled }); // prettier-ignore
}

export function useApiKeyChanges() {
  const qc = useQueryClient();
  const { tenant } = useSession();
  const done = () => qc.invalidateQueries({ queryKey: [...key(tenant?.id), 'keys'] });
  return {
    create: useMutation({ mutationFn: (body: CreateApiKey) => api<CreatedApiKey>('POST', '/settings/api-keys', body), onSuccess: done }), // prettier-ignore
    revoke: useMutation({ mutationFn: (id: string) => api<ApiKey>('POST', `/settings/api-keys/${id}/revoke`), onSuccess: done }), // prettier-ignore
  };
}

export function useWebhooks() {
  const { tenant } = useSession();
  return useQuery({ queryKey: [...key(tenant?.id), 'webhooks'], queryFn: () => api<Webhook[]>('GET', '/settings/webhooks'), enabled: !!tenant }); // prettier-ignore
}

export function useDeliveries(id: string | null) {
  const { tenant } = useSession();
  return useQuery({
    queryKey: [...key(tenant?.id), 'webhooks', id, 'deliveries'],
    queryFn: () => api<WebhookDelivery[]>('GET', `/settings/webhooks/${id}/deliveries`),
    enabled: !!tenant && !!id,
  });
}

export function useWebhookChanges() {
  const qc = useQueryClient();
  const { tenant } = useSession();
  const done = () => qc.invalidateQueries({ queryKey: [...key(tenant?.id), 'webhooks'] });
  return {
    create: useMutation({ mutationFn: (body: CreateWebhook) => api<WebhookWithSecret>('POST', '/settings/webhooks', body), onSuccess: done }), // prettier-ignore
    update: useMutation({ mutationFn: ({ id, ...body }: UpdateWebhook & { id: string }) => api<Webhook>('PATCH', `/settings/webhooks/${id}`, body), onSuccess: done }), // prettier-ignore
    rotate: useMutation({ mutationFn: (id: string) => api<WebhookWithSecret>('POST', `/settings/webhooks/${id}/rotate`), onSuccess: done }), // prettier-ignore
    test: useMutation({ mutationFn: (id: string) => api<WebhookDelivery>('POST', `/settings/webhooks/${id}/test`), onSuccess: done }), // prettier-ignore
    retry: useMutation({ mutationFn: ({ id, delivery }: { id: string; delivery: string }) => api<WebhookDelivery>('POST', `/settings/webhooks/${id}/deliveries/${delivery}/retry`), onSuccess: done }), // prettier-ignore
  };
}
